import { useEffect, useRef } from 'react'
import { useApiAuth } from '@/modules/auth-api'
import { useCartStore } from '@/modules/cart'
import type { CartItem } from '@/modules/cart'
import {
  useServerCart,
  useAddToServerCart,
  useUpdateServerCartQty,
  useRemoveServerCartItem,
  useClearServerCart,
  useMergeServerCart,
} from './useServerCart'

/**
 * 2-chiều sync giữa server cart (khi login) và Zustand local store.
 * - Guest: không làm gì, local hoạt động như cũ.
 * - Login: 1 lần MERGE local → server, rồi PULL server xuống store.
 *   Sau đó PUSH mọi delta local lên server.
 *
 * suppressPush = cờ "đang pull, đừng push" được clear bằng setTimeout
 * để chắc chắn vượt qua chu kỳ render tiếp theo (tránh effect push bắn trùng).
 */
export function useCartSync() {
  const isAuthed = useApiAuth((s) => s.isAuthed)
  const items = useCartStore((s) => s.items)
  const serverQ = useServerCart()
  const addMut = useAddToServerCart()
  const updMut = useUpdateServerCartQty()
  const rmMut = useRemoveServerCartItem()
  const clrMut = useClearServerCart()
  const mergeMut = useMergeServerCart()

  const prevAuthed = useRef(isAuthed)
  const mergedOnce = useRef(false)
  const suppressPush = useRef(false)
  const prevItems = useRef<CartItem[]>(items)
  const lastServerSig = useRef<string>('') // tránh pull lặp khi data không đổi

  // 1) Khi login lần đầu → merge local → server, rồi pull
  useEffect(() => {
    const justLoggedIn = !prevAuthed.current && isAuthed
    prevAuthed.current = isAuthed
    if (!isAuthed) {
      mergedOnce.current = false
      lastServerSig.current = ''
      return
    }
    if (justLoggedIn && !mergedOnce.current) {
      mergedOnce.current = true
      const run = async () => {
        if (items.length > 0) {
          await mergeMut.mutateAsync({
            items: items.map((it) => ({ productId: it.id, qty: it.qty })),
          })
        }
        suppressPush.current = true
        useCartStore.setState({ items: [] })
        prevItems.current = []
        await serverQ.refetch()
      }
      run().catch(() => { mergedOnce.current = false })
    }
  }, [isAuthed, items, mergeMut, serverQ])

  // 2) Pull server → store (chỉ khi đã login)
  useEffect(() => {
    if (!isAuthed) return
    if (!serverQ.data) return
    const next: CartItem[] = (serverQ.data.items ?? []).map((it) => ({
      id: it.productId,
      slug: it.slug,
      name: it.name,
      brand: it.brand,
      image: it.image,
      price: it.price,
      oldPrice: it.oldPrice,
      qty: it.qty,
    }))
    const sig = next.map((n) => `${n.id}:${n.qty}`).sort().join('|')
    if (sig === lastServerSig.current) return // không có gì mới
    lastServerSig.current = sig

    suppressPush.current = true
    useCartStore.setState({ items: next })
    prevItems.current = next
    // clear sau 50ms để chắc chắn effect #3 chạy xong nhìn thấy suppress=true
    setTimeout(() => { suppressPush.current = false }, 50)
  }, [isAuthed, serverQ.data])

  // 3) Push store delta → server (chỉ khi login, không suppress)
  useEffect(() => {
    if (!isAuthed) { prevItems.current = items; return }
    if (suppressPush.current) { prevItems.current = items; return }

    const prev = prevItems.current
    prevItems.current = items

    // Clear all
    if (prev.length > 0 && items.length === 0) {
      clrMut.mutate()
      return
    }
    // Remove 1
    const removed = prev.find((p) => !items.find((c) => c.id === p.id))
    if (removed) {
      const srv = serverQ.data?.items.find((s) => s.productId === removed.id)
      if (srv) rmMut.mutate(srv.id)
      return
    }
    // Add 1
    const added = items.find((c) => !prev.find((p) => p.id === c.id))
    if (added) {
      addMut.mutate({ productId: added.id, qty: added.qty })
      return
    }
    // Change qty
    const changed = items.find((c) => {
      const p = prev.find((x) => x.id === c.id)
      return p && p.qty !== c.qty
    })
    if (changed) {
      const srv = serverQ.data?.items.find((s) => s.productId === changed.id)
      if (srv) updMut.mutate({ itemId: srv.id, qty: changed.qty })
    }
  }, [isAuthed, items, serverQ.data, addMut, updMut, rmMut, clrMut])
}
